<script>
import { toRef } from 'vue'
import useContract from '../../../../Composables/Contract/useContract.ts'
import Badge from '../UI/Badge.vue'
import SimpleGroupSelect from '../UI/Form/SimpleGroupSelect.vue'
import mixinComponent from '../../Mixins/mixinComponent'
import { globalVariablesMixin } from '../../Mixins/global'
import { platformMixins } from '../../Mixins/platform'
import LazyContent from '../UI/Loading/LazyContent.vue'
import ContractMenuItem from './ContractMenuItem.vue'

export default {
  components: { LazyContent, ContractMenuItem, SimpleGroupSelect, Badge },
  mixins: [mixinComponent, globalVariablesMixin, platformMixins],

  data: () => ({
    navigationBlocked: false,
  }),

  props: {
    active: { type: String, required: true },
    contract: { type: [Object, null], required: true },
    alertInformation: { type: Object, required: false },
  },

  computed: {
    isTender() {
      return this.contract?.finance.tender
    },
    isWallet() {
      return ['PAID', 'FINISH', 'ENDED', 'CLOSED', 'PROLONGATION', 'CESSION'].includes(this.contract?.status)
    },
    url() {
      let urlParams = new URLSearchParams(window.location.search)
      let urlPath = window.location.pathname
      let context = !this.isWallet ? '/contrats/' : '/portefeuille/'
      urlPath = urlPath.split(context)[1]
      return urlPath + '?i=' + this.contract?.id + '&p=' + urlParams.get('p')
    },
    hideRestructuration() {
      return this.contract?.status !== 'FINISH'
    },
    showSetup() {
      return ['DELIVERED', 'COMPLETE'].includes(this.contract?.status)
    },
    showCession() {
      return this.contract?.status === 'COMPLETE'
    },
    outstandingMenuAvailable() {
      return this.hasRole('ROLE_OUTSTANDING_SHOW') && this.isWalletStatus(this.contract?.status)
    },
    numberClasses() {
      return {
        'col-md-4': !this.isTender,
        'col-md-3': this.isTender,
        'd-flex': true,
        'align-items-center': true,
      }
    },
    statusClasses() {
      return {
        'col-md-4': !this.isTender && !this.isTheme('lbp'),
        col: this.isTender || this.isTheme('lbp'),
        'd-flex': true,
        'align-items-center': true,
        'justify-content-center': true,
      }
    },
    menuItemClasses() {
      return {
        'col-md-4': !this.isTender,
        col: this.isTender,
        'device-menu': true,
        'justify-content-end': true,
      }
    },
    isActiveAlert() {
      return this.alertInformation?.status !== 'ARCHIVED' && this.alertInformation?.number > 0
    },
    alertNumberClasses() {
      return {
        'topbar-alert': true,
        [`topbar-alert-${this.alertInformation?.blocking ? 'danger' : 'warning'}`]: true,
      }
    },
    alertClasses() {
      return {
        'topbar-contract-circle': true,
        'bell-container': this.isActiveAlert,
      }
    },
    displayOutstanding() {
      return this.hasRole('ROLE_OUTSTANDING_CLIENT') && this.contract?.status === 'FINISH'
    },
    displayAction() {
      return (
        conf.action &&
        (this.isClient ||
          (this.isPlatform && !this.isCompany) ||
          (this.isCompany && this.hasRole('ROLE_CONTRACT_ACTION')))
      )
    },
    isBusinessMan() {
      return user?.isBusinessMan ?? false
    },
    getDocumentNumber() {
      return this.displayOutstanding ? 3 : 2
    },
    getActionNumber() {
      return this.displayOutstanding ? 4 : 3
    },
    availableMenuItems() {
      return this.menuItems.filter(({ show = true }) => show)
    },
    tabItems() {
      return this.availableMenuItems.filter(({ number = null }) => number !== null)
    },
    actionItems() {
      return this.availableMenuItems.filter(({ number = null, show = true }) => number === null && show === true)
    },
    hasActiveItem() {
      return this.availableMenuItems.some(({ active, disabled = false }) => active && !disabled)
    },
    menuItems() {
      let items

      if (!this.isWallet) {
        items = [
          {
            name: 'contract.information.menu',
            number: 1,
            link: 'contract',
            disabled: false,
            active: this.active === 'contract',
          },
          {
            name: 'contract.information.activeAndServices',
            number: 2,
            link: 'active-and-services',
            disabled: false,
            active: this.active === 'active-and-services',
          },
          {
            name: 'contract.document.menu',
            number: 3,
            link: 'documents',
            disabled: false,
            active: this.active === 'documents',
          },
          {
            show: this.hasRole('ROLE_DELIVERY_SHOW'),
            name: 'contract.delivery.menu',
            number: 4,
            link: 'delivery',
            disabled: !this.contract?.isDeliveryAvailable,
            active: this.active === 'delivery',
          },
          {
            show: conf.setup && this.isBusinessMan,
            name: 'contract.setup.menu',
            number: 5,
            link: 'setup',
            disabled: !this.showSetup,
            active: this.active === 'setup',
          },
          {
            show: conf.cession && this.isBusinessMan,
            name: 'contract.cession.menu',
            number: 6,
            link: 'cession',
            disabled: !this.showCession,
            active: this.active === 'cession',
          },
          {
            show: this.hasRole('ROLE_RESTRUCTURING') && this.isPartOfRestructuring,
            name: 'simulation.menu.restructuring',
            link: 'restructuring',
            icon: 'fa fa-refresh',
            disabled: false,
            active: this.active === 'restructuring',
          },
          {
            show: this.isBusinessMan && this.hasRole('ROLE_PROFITABILITY_SHOW'),
            name: 'contract.information.profitability',
            icon: 'fa fa-piggy-bank',
            link: 'profitability',
            disabled: false,
            active: this.active === 'profitability',
          },
          {
            show: this.outstandingMenuAvailable,
            name: 'contract.information.outstandings',
            icon: 'fa fa-list-alt',
            link: 'outstanding',
            disabled: false,
            active: this.active === 'outstanding',
          },
          {
            show: this.hasRole('ROLE_CONTRIBUTORS_SHOW'),
            name: 'contract.information.actors',
            icon: 'fas fa-user',
            link: 'actors',
            disabled: false,
            active: this.active === 'actors',
          },
          {
            show: this.hasRole('ROLE_COMMENTS_SHOW'),
            name: 'contract.information.comments',
            icon: 'fas fa-comment',
            link: 'comments',
            disabled: false,
            active: this.active === 'comments',
          },
        ]
      } else {
        items = [
          {
            name: 'contract.client.detail.menu',
            link: 'detail',
            number: 1,
            disabled: false,
            active: this.active === 'detail',
          },
          {
            show: this.displayOutstanding,
            name: 'contract.outstanding.title',
            link: 'contractOutstanding',
            number: 2,
            disabled: false,
            active: this.active === 'contractOutstanding',
          },
          {
            name: 'contract.client.document.menu',
            link: 'document',
            number: this.getDocumentNumber,
            disabled: false,
            active: this.active === 'document',
          },
          {
            show: this.displayAction,
            name: 'contract.client.action.menu',
            link: 'action',
            number: this.getActionNumber,
            disabled: false,
            active: this.active === 'action',
          },
          {
            show: this.isBusinessMan && this.hasRole('ROLE_PROFITABILITY_SHOW'),
            name: 'contract.information.profitability',
            number: null,
            icon: 'fa fa-piggy-bank',
            link: 'profitability',
            disabled: false,
            active: this.active === 'profitability',
          },
          {
            show: this.hasRole('ROLE_RESTRUCTURATION'),
            name: 'contract.information.restructuration',
            link: 'restructuration',
            icon: 'fas fa-sync-alt',
            disabled: this.hideRestructuration,
            active: this.active === 'restructuration',
          },
          {
            show: this.hasRole('ROLE_RESTRUCTURING') && this.isPartOfRestructuring,
            name: 'simulation.menu.restructuring',
            link: 'restructuring',
            icon: 'fa fa-refresh',
            disabled: false,
            active: this.active === 'restructuring',
          },
          {
            show: this.hasRole('ROLE_COMMENTS_SHOW'),
            name: 'contract.information.comments',
            link: 'comments',
            icon: 'fas fa-comment',
            disabled: false,
            active: this.active === 'comments',
          },
        ]
      }

      items.push({
        show: this.hasRole('ROLE_ALERT_SHOW'),
        name: 'simulation.menu.alert',
        icon: 'fas fa-bell',
        link: 'alert',
        disabled: false,
        classes: {
          'bell-container': this.isActiveAlert,
        },
        badge: !this.isActiveAlert
          ? null
          : {
              class: this.alertNumberClasses,
              value: this.alertInformation?.number,
            },
      })

      return items
    },
  },

  methods: {
    ensureDefaultMenuItem() {
      const firstAvailableMenuItem = this.availableMenuItems[0]

      if (!this.hasActiveItem && firstAvailableMenuItem && this.contract?.id) {
        this.handleNavigation(firstAvailableMenuItem.link)
      }
    },
    handleNavigation(page) {
      if (this.navigationBlocked) {
        return
      }

      if (page === 'alert') {
        this.showAlert()
        return
      }

      this.navigationBlocked = true // Block subsequent calls

      const urlPath = window.location.pathname
      const context = !this.isWallet ? '/contrats/' : '/portefeuille/'
      const pathAfterContext = urlPath.split(context)[1]
      const basePath = pathAfterContext?.split('?')[0] || (this.isWallet ? 'portefeuille.php' : 'contract.php')

      window.history.pushState({ p: page }, 'Title', `${context}${basePath}?i=${this.contract.id}&p=${page}`)
      this.$emit('link', page)

      setTimeout(() => {
        this.navigationBlocked = false
      }, 200)
    },
    showAlert() {
      this.$emit('show-alert')
    },
  },
  setup(props) {
    const contract = toRef(props, 'contract')
    const { isPartOfRestructuring } = useContract(contract)

    return { isPartOfRestructuring }
  },
  watch: {
    contract: {
      immediate: true,
      handler() {
        this.ensureDefaultMenuItem()
      },
    },
  },
  mounted() {
    this.ensureDefaultMenuItem()
  },
}
</script>
<template>
  <div class="topbar-submenu">
    <LazyContent :loading="!contract">
      <div class="topbar-contract-title">
        <div
          :class="numberClasses"
          v-html="$t('contract.title.number', { number: contract.number })"
          e2e-data="contractNumber"
        ></div>
        <div
          :class="statusClasses"
          v-html="
            $t('contract.title.statuses', {
              status: contract.status ? $t('contract.status.' + contract.status + '.label') : '',
            })
          "
          e2e-data="contractStatus"
        ></div>
        <div v-if="isTender" :class="statusClasses">
          <Badge type="warning">
            {{ $t('simulation.fields.finance.tender.label') }}
          </Badge>
        </div>
        <div v-if="isTheme('lbp')" :class="statusClasses">
          <Badge type="warning" e2e-data="contractWithoutOrder">
            {{ $t(contract.withoutOrder ? 'contract.withoutOrder' : 'contract.withOrder') }}
          </Badge>
        </div>
        <div :class="menuItemClasses">
          <div v-for="item in actionItems" :key="item.name">
            <ContractMenuItem :menu-item="item" @navigate="handleNavigation" :menu-name="item.name">
              <template v-if="!!item.badge" #badge>
                <span :class="item.badge.class">{{ item.badge.value }}</span>
              </template>
            </ContractMenuItem>
          </div>
        </div>
      </div>

      <hr class="my-1" style="background-color: white" />

      <div class="row mobile-menu justify-content-center">
        <SimpleGroupSelect
          v-model="url"
          :label-text="$t('menu.select')"
          :pre-select-value="false"
          :value="active"
          @transmitted="handleNavigation"
        >
          <option v-for="item in availableMenuItems" :key="item.name" :value="item.link">
            {{ $t(item.name) }}
          </option>
        </SimpleGroupSelect>
      </div>

      <div class="device-menu justify-content-center">
        <div v-for="item in tabItems" :key="item.name">
          <ContractMenuItem :menu-item="item" @navigate="handleNavigation" :menu-name="item.name" />
        </div>
      </div>
      <template #loader>
        <div class="topbar-contract-title m-2">
          <div class="flex-grow-1 text-center">
            {{ $t('loading') }}
          </div>
        </div>
      </template>
    </LazyContent>
  </div>
</template>
